/**
 * Cart drawer ship-date picker.
 *
 * Renders as an always-visible calendar inline in the cart drawer (see
 * snippets/cart-ship-date-picker.liquid) - no click-to-expand. An earlier
 * version used a collapsed "Select a ship date" button that had to be
 * clicked to open a floating calendar; Kevin asked for it to always be
 * visible instead once he tested that version, so this reverts to a plain
 * inline calendar, which also removes the floating-popover positioning
 * code entirely (nothing left to mis-position).
 *
 * Availability data comes from the "Ship Date" metaobject, embedded
 * server-side as JSON by the Liquid snippet - no dates are hardcoded here.
 * Past dates are excluded server-side in the Liquid snippet itself
 * (compared against Liquid's `now`), not here.
 *
 * On selection, writes the chosen date to the cart's custom attribute
 * "ship_date" (exact key required by a downstream system) formatted as
 * MM/DD/YYYY via /cart/update.js, preserving any other existing cart
 * attributes. Does not set any order tag - that is out of scope here and is
 * handled separately via Shopify Flow.
 *
 * Also installs a document-level guard (see bottom of file) that blocks
 * the cart's checkout button when a cookie is in the cart and no ship date
 * has been picked yet, per Kevin's explicit request after testing. It
 * targets the checkout control by `[name="checkout"]` / a `/checkout`
 * link, which is how Shopify's own cart forms mark the checkout button
 * across themes (confirmed in this theme's snippets/cart-drawer.liquid:
 * `id="CartDrawer-Checkout" name="checkout" form="CartDrawer-Form"`), so
 * it doesn't depend on any one theme's exact markup or class names.
 *
 * A header chevron lets the shopper minimize the whole widget down to the
 * title bar (see updateMinimizedState/expand below), independent of the
 * selected/edit collapse above, so they can glance at the rest of the cart
 * without a full calendar in the way. If checkout is blocked while the
 * widget is minimized, it auto-expands so the error is actually visible.
 *
 * This same component is also used standalone on the Create Your Own page
 * (see assets/create-your-own-collection.js), not just inside the cart
 * drawer. Several things support that:
 *  - startCollapsed (data-start-collapsed, CYO only): shows the compact
 *    "Select a ship date" row even before a date is picked, instead of
 *    the open calendar. openCalendar() forces it open again (used by the
 *    Edit/Select button, the checkout guard, and CYO's own Add to Cart
 *    guard). The cart drawer does NOT set this - it keeps its existing
 *    always-open-until-picked behavior, per Kevin's explicit feedback
 *    described above; do not change that default.
 *  - setRequiredQuantity(n): lets an owning page tell this instance how
 *    many cookies the current order needs, so a date only shows as
 *    pickable when it has enough remaining capacity (capacity - booked),
 *    not just when it isn't full yet. Clears an already-selected date
 *    that no longer fits and re-opens the calendar rather than silently
 *    keeping an invalid selection.
 *  - refreshAvailability(): re-fetches this page's own section HTML to
 *    pick up current booked-count data instead of trusting whatever was
 *    baked in at initial page render, showing a loading state meanwhile.
 *    Requires data-refresh-url on the element; not every context uses
 *    this (the cart drawer deliberately doesn't call it - see
 *    cart-ship-date-picker.liquid).
 * A 'ship-date-picker:change' custom event (bubbles) fires with
 * { selectedDate } whenever the selection changes or gets cleared, so an
 * owning page can react (e.g. gate its own Add to Cart button) without
 * this file needing to know anything about that page's UI.
 */
(function () {
  if (customElements.get('cart-ship-date-picker')) return;

  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  class CartShipDatePicker extends HTMLElement {
    constructor() {
      super();
      this.selectedDate = null; // 'YYYY-MM-DD' or null
      this.availability = new Map(); // 'YYYY-MM-DD' -> { available, capacity, booked, blocked }
      this.viewYear = null;
      this.viewMonth = null; // 0-based
      this.isUpdating = false;
      this.isEditing = false; // true = calendar forced open even if a date is already selected
      this.isMinimized = false; // true = whole widget collapsed to just the header bar
      this.isLoading = false; // true while refreshAvailability() has an in-flight fetch
      // How many cookies this selection needs to fit on a given date. 0 means
      // no quantity constraint (just blocked/fully-booked/past-date checks).
      // Set from data-required-quantity at connect, and updated live via
      // setRequiredQuantity() by whoever owns this instance (e.g. the
      // Create Your Own page recalculates it as the shopper builds a pack).
      this.requiredQuantity = 0;
    }

    connectedCallback() {
      this.minimizeBtn = this.querySelector('[data-ship-date-minimize]');
      this.bodyEl = this.querySelector('[data-ship-date-body]');
      this.summaryEl = this.querySelector('[data-ship-date-summary]');
      this.summaryTextEl = this.querySelector('[data-ship-date-summary-text]');
      this.editBtn = this.querySelector('[data-ship-date-edit]');
      this.calendarEl = this.querySelector('[data-ship-date-calendar]');
      this.grid = this.querySelector('[data-ship-date-grid]');
      this.monthLabel = this.querySelector('[data-ship-date-month-label]');
      this.prevBtn = this.querySelector('[data-ship-date-prev]');
      this.nextBtn = this.querySelector('[data-ship-date-next]');
      this.errorEl = this.querySelector('[data-ship-date-error]');
      this.loadingEl = this.querySelector('[data-ship-date-loading]');

      this.loadAvailability();
      this.requiredQuantity = Number(this.dataset.requiredQuantity) || 0;
      // Whether "no date picked yet" starts collapsed (compact "Select a
      // ship date" row) instead of the open calendar. Only set true via
      // data-start-collapsed (Create Your Own page) - the cart drawer
      // deliberately leaves this false so it keeps its existing
      // always-open-until-picked behavior. See cart-ship-date-picker.liquid.
      this.startCollapsed = this.dataset.startCollapsed === 'true';
      this.editLabelEl = this.querySelector('[data-ship-date-edit-label]');

      const selectedAttr = this.dataset.selected;
      this.selectedDate = selectedAttr ? this.mmddyyyyToIso(selectedAttr) : null;

      const today = new Date();
      // Floor for calendar navigation: never let the shopper browse to a
      // month before the current one, since every date in it would be
      // unavailable anyway.
      this.minYear = today.getFullYear();
      this.minMonth = today.getMonth();

      if (this.selectedDate) {
        const [y, m] = this.selectedDate.split('-').map(Number);
        this.viewYear = y;
        this.viewMonth = m - 1;
      } else {
        // Kevin's bug report: this used to always default to today's
        // calendar month, even when nothing in that month was available
        // (e.g. today is Sep 29-30 with no September dates left) - the
        // shopper would open the calendar to a wall of disabled days with
        // no clue that October had open dates. Default to the month
        // containing the first actually-available date instead, falling
        // back to today's month only if nothing is available at all.
        const firstAvailable = this.findFirstAvailableDate();
        if (firstAvailable) {
          const [y, m] = firstAvailable.split('-').map(Number);
          this.viewYear = y;
          this.viewMonth = m - 1;
        } else {
          this.viewYear = this.minYear;
          this.viewMonth = this.minMonth;
        }
      }
      // Never open on a month before the floor, even if a stale selected
      // date somehow points to the past.
      if (this.viewYear * 12 + this.viewMonth < this.minYear * 12 + this.minMonth) {
        this.viewYear = this.minYear;
        this.viewMonth = this.minMonth;
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.changeMonth(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.changeMonth(1));
      if (this.editBtn) {
        this.editBtn.addEventListener('click', () => this.openCalendar());
      }
      if (this.summaryEl) {
        // Kevin's bug report: the only clickable area on the collapsed
        // "Select a ship date" row was the small edit button/icon, not the
        // row itself - easy to miss/mistap. Make the whole summary row
        // open the calendar too, while still letting the edit button's own
        // listener above handle clicks on itself without double-firing.
        this.summaryEl.addEventListener('click', (event) => {
          if (event.target.closest('[data-ship-date-edit]')) return;
          this.openCalendar();
        });
      }
      if (this.minimizeBtn) {
        this.minimizeBtn.addEventListener('click', () => {
          this.isMinimized = !this.isMinimized;
          this.updateMinimizedState();
        });
      }

      this.updateCollapsedState();
      this.updateMinimizedState();
      this.render();
    }

    // Shows the compact summary row and hides the calendar once a date is
    // picked, unless the shopper clicked Edit/Select - Kevin's explicit
    // ask: don't keep a full calendar showing after the choice is made.
    // When startCollapsed is set (Create Your Own page only), the SAME
    // compact row also shows before a date is picked, just with a
    // "Select a ship date" prompt instead of "Selected ship date: ...";
    // without it (cart drawer), no date yet means the calendar stays
    // open, unchanged from before.
    updateCollapsedState() {
      const isCollapsed = this.isEditing ? false : !!(this.selectedDate || this.startCollapsed);

      if (this.summaryEl) this.summaryEl.hidden = !isCollapsed;
      if (this.calendarEl) this.calendarEl.hidden = isCollapsed;

      if (this.summaryTextEl) {
        this.summaryTextEl.textContent = this.selectedDate
          ? `Selected ship date: ${this.isoToMmddyyyy(this.selectedDate)}`
          : 'Select a ship date';
      }
      if (this.editBtn) {
        this.editBtn.setAttribute('aria-label', this.selectedDate ? 'Change ship date' : 'Select a ship date');
      }
      if (this.editLabelEl) {
        this.editLabelEl.textContent = this.selectedDate ? 'Edit' : 'Select';
      }
    }

    // Opens the calendar (forces it visible even when collapsed), used by
    // the Edit/Select button, the checkout guard, and an owning page (e.g.
    // Create Your Own) when it needs to draw attention to an unpicked date.
    openCalendar() {
      this.isEditing = true;
      this.updateCollapsedState();
    }

    // Lets the shopper collapse the whole widget (summary row or calendar,
    // whichever is currently showing) down to just the header bar, so they
    // can see the rest of the cart without scrolling past it first.
    updateMinimizedState() {
      if (this.bodyEl) this.bodyEl.hidden = this.isMinimized;
      if (this.minimizeBtn) {
        this.minimizeBtn.setAttribute('aria-expanded', String(!this.isMinimized));
        this.minimizeBtn.setAttribute(
          'aria-label',
          this.isMinimized ? 'Expand ship date picker' : 'Collapse ship date picker'
        );
        this.minimizeBtn.classList.toggle('is-collapsed', this.isMinimized);
      }
    }

    // Forces the widget back open. Used when checkout is blocked while the
    // shopper had it minimized - otherwise they'd never see the error.
    expand() {
      if (!this.isMinimized) return;
      this.isMinimized = false;
      this.updateMinimizedState();
    }

    loadAvailability() {
      this.availability = new Map();
      try {
        const script = this.querySelector('[data-ship-date-availability]');
        if (!script || !script.textContent.trim()) return;
        const data = JSON.parse(script.textContent);
        if (!Array.isArray(data)) return;
        data.forEach((entry) => {
          if (entry && entry.date) this.availability.set(entry.date, entry);
        });
      } catch (error) {
        // Fail gracefully: leave availability empty (calendar renders all
        // dates disabled) rather than breaking the rest of the cart drawer.
        console.error('[ship-date-picker] failed to parse availability data', error);
      }
    }

    changeMonth(delta) {
      let nextMonth = this.viewMonth + delta;
      let nextYear = this.viewYear;
      if (nextMonth < 0) {
        nextMonth = 11;
        nextYear -= 1;
      } else if (nextMonth > 11) {
        nextMonth = 0;
        nextYear += 1;
      }
      // Never navigate to a month before the current one - every date in
      // it would show as unavailable anyway.
      if (nextYear * 12 + nextMonth < this.minYear * 12 + this.minMonth) return;
      this.viewMonth = nextMonth;
      this.viewYear = nextYear;
      this.render();
    }

    mmddyyyyToIso(value) {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value || '').trim());
      if (!match) return null;
      const [, mm, dd, yyyy] = match;
      return `${yyyy}-${mm}-${dd}`;
    }

    isoToMmddyyyy(iso) {
      const [y, m, d] = iso.split('-');
      return `${m}/${d}/${y}`;
    }

    // Returns the earliest 'YYYY-MM-DD' in this.availability that is
    // actually available (not blocked/fully-booked/past), or null if none
    // are. Used at connect time (see connectedCallback) so the calendar
    // opens showing a month that actually has something to pick, instead
    // of always defaulting to today's month even when every date in it is
    // unavailable (Kevin's Sep 30 bug report: today was Sep 29-30 with no
    // September dates left, so the calendar opened on an all-unavailable
    // month with no indication anything was pickable in October).
    findFirstAvailableDate() {
      const dates = Array.from(this.availability.keys()).sort();
      for (const iso of dates) {
        const entry = this.availability.get(iso);
        if (entry && entry.available) return iso;
      }
      return null;
    }

    render() {
      if (!this.grid) return;

      if (this.monthLabel) {
        this.monthLabel.textContent = `${MONTH_NAMES[this.viewMonth]} ${this.viewYear}`;
      }
      const atFloor = this.viewYear * 12 + this.viewMonth <= this.minYear * 12 + this.minMonth;
      if (this.prevBtn) this.prevBtn.disabled = this.isUpdating || this.isLoading || atFloor;
      if (this.nextBtn) this.nextBtn.disabled = this.isUpdating || this.isLoading;

      this.grid.innerHTML = '';

      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const startWeekday = firstOfMonth.getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();

      const fragment = document.createDocumentFragment();

      for (let i = 0; i < startWeekday; i++) {
        const filler = document.createElement('span');
        filler.className = 'ship-date-picker__day is-empty';
        filler.setAttribute('role', 'presentation');
        filler.setAttribute('aria-hidden', 'true');
        fragment.appendChild(filler);
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const mm = String(this.viewMonth + 1).padStart(2, '0');
        const dd = String(day).padStart(2, '0');
        const iso = `${this.viewYear}-${mm}-${dd}`;
        const entry = this.availability.get(iso);
        const remaining = entry ? Number(entry.capacity) - Number(entry.booked) : 0;
        // entry.available already covers blocked/fully-booked/past-date
        // (computed server-side in the Liquid snippet). On top of that,
        // when this instance has a requiredQuantity set (e.g. the Create
        // Your Own page tracks how many cookies are currently being
        // ordered), a date also has to have enough remaining capacity to
        // fit that quantity, not just be "not full yet".
        const meetsQuantity = this.requiredQuantity > 0 ? remaining >= this.requiredQuantity : true;
        const isAvailable = !!(entry && entry.available && meetsQuantity) && !this.isLoading;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ship-date-picker__day';
        btn.setAttribute('role', 'gridcell');
        btn.textContent = String(day);
        btn.disabled = !isAvailable || this.isUpdating || this.isLoading;

        let statusLabel = 'not open for shipping';
        if (entry) {
          if (entry.blocked) {
            statusLabel = 'blocked';
          } else if (Number(entry.booked) >= Number(entry.capacity)) {
            statusLabel = 'fully booked';
          } else if (!meetsQuantity) {
            statusLabel = `only ${remaining} left, not enough for your order`;
          } else {
            statusLabel = 'available';
          }
        }
        const label = `${MONTH_NAMES[this.viewMonth]} ${day}, ${this.viewYear}, ${statusLabel}`;
        btn.setAttribute('aria-label', label);
        btn.title = label;

        const isSelected = iso === this.selectedDate;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', String(isSelected));

        if (isAvailable) {
          btn.addEventListener('click', () => this.selectDate(iso));
        }

        fragment.appendChild(btn);
      }

      this.grid.appendChild(fragment);
    }

    async selectDate(iso) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.hideError();
      this.render();

      const formatted = this.isoToMmddyyyy(iso);

      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) throw new Error('Failed to read current cart');
        const cart = await cartResponse.json();

        // Merge into existing attributes so we don't clobber anything else
        // already stored on the cart.
        const attributes = Object.assign({}, cart.attributes || {}, { ship_date: formatted });

        const updateResponse = await fetch(window.routes ? window.routes.cart_update_url : '/cart/update.js', {
          ...(typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
          body: JSON.stringify({ attributes: attributes }),
        });

        if (!updateResponse.ok) throw new Error('Failed to update cart attributes');

        this.selectedDate = iso;
        this.isEditing = false;
        this.updateCollapsedState();
        this.hideError();
        this.dispatchChange();

        if (typeof window.refreshCartDrawer === 'function') {
          await window.refreshCartDrawer();
        }
      } catch (error) {
        console.error('[ship-date-picker] failed to save ship date', error);
        this.showError("Couldn't save your ship date. Please try again.");
      } finally {
        this.isUpdating = false;
        this.render();
      }
    }

    // Lets an owning page (e.g. Create Your Own's pack builder) tell this
    // picker how many cookies the current order needs, so the calendar can
    // filter out dates that don't have enough remaining capacity. If the
    // currently selected date no longer fits, the selection is cleared and
    // the calendar re-opens so the shopper has to pick again - silently
    // keeping an invalid selection would let them proceed with a date that
    // can't actually take their order.
    setRequiredQuantity(quantity) {
      const next = Number(quantity) || 0;
      if (next === this.requiredQuantity) return;
      this.requiredQuantity = next;
      this.revalidateSelection();
      this.render();
    }

    // Clears the current selection (with an error and the calendar
    // re-opened) if it no longer holds up against this.availability /
    // this.requiredQuantity - called after either changes, so a shopper is
    // never left with a date silently selected that can't actually take
    // their order.
    revalidateSelection() {
      if (!this.selectedDate) return;
      const entry = this.availability.get(this.selectedDate);
      const remaining = entry ? Number(entry.capacity) - Number(entry.booked) : 0;
      const stillFits = !!(entry && entry.available) && (this.requiredQuantity <= 0 || remaining >= this.requiredQuantity);
      if (stillFits) return;

      this.selectedDate = null;
      this.isEditing = true;
      this.updateCollapsedState();
      this.showError('That date can no longer fit your order size. Please pick another date.');
      this.dispatchChange();
    }

    // Re-fetches this page's own section HTML (same technique
    // refreshCartDrawer() uses elsewhere in this theme) to pick up
    // whatever the "Ship Date" metaobjects currently show, rather than
    // trusting whatever was baked into the page at initial render. Needs
    // data-refresh-url on the element (set by the Liquid snippet); if it's
    // missing, this just no-ops instead of erroring - not every context
    // that uses this component needs live refresh (the cart drawer, for
    // example, intentionally does not call this - see cart-ship-date-
    // picker.liquid).
    async refreshAvailability() {
      const url = this.dataset.refreshUrl;
      if (!url) return;

      this.setLoading(true);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to refresh ship date availability');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const freshScript = doc.querySelector('[data-ship-date-availability]');
        const ownScript = this.querySelector('[data-ship-date-availability]');
        if (freshScript && ownScript) {
          ownScript.textContent = freshScript.textContent;
        }
        this.loadAvailability();
        this.revalidateSelection();
      } catch (error) {
        console.error('[ship-date-picker] failed to refresh availability', error);
        // Fail gracefully: keep whatever availability data was already
        // loaded rather than breaking the calendar.
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (this.loadingEl) this.loadingEl.hidden = !isLoading;
      if (this.calendarEl) this.calendarEl.setAttribute('aria-busy', String(isLoading));
      this.render();
    }

    dispatchChange() {
      this.dispatchEvent(
        new CustomEvent('ship-date-picker:change', {
          bubbles: true,
          detail: { selectedDate: this.selectedDate },
        })
      );
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.classList.add('has-error');
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.classList.remove('has-error');
    }
  }

  customElements.define('cart-ship-date-picker', CartShipDatePicker);

  // --- Checkout guard -------------------------------------------------
  // Blocks checkout whenever this picker is on the page (meaning a cookie
  // is in the cart, per the Liquid snippet's own tag check) and no date
  // has been picked yet. Targets [name="checkout"], which is how Shopify
  // marks the checkout control on cart forms across themes, so this isn't
  // tied to any one theme's markup.
  //
  // Since the Create Your Own page now also renders its own standalone
  // picker instance (see cart-ship-date-picker.liquid, always_show), a
  // page can have TWO <cart-ship-date-picker> elements in the DOM at
  // once: that standalone one and the drawer's own (once the drawer has
  // rendered with cookies in the cart). A checkout attempt only ever
  // happens from the cart drawer or the cart page, never from the CYO
  // page itself, so this looks for the picker inside whichever of those
  // two contains the control that was actually clicked/submitted first,
  // falling back to a document-wide search (today's behavior, correct
  // everywhere else this picker is used) only when neither is found.
  function findUnselectedPicker(contextEl) {
    let scope = document;
    if (contextEl && typeof contextEl.closest === 'function') {
      scope = contextEl.closest('cart-drawer, cart-items') || document;
    }
    let picker = scope.querySelector('cart-ship-date-picker');
    if (!picker && scope !== document) picker = document.querySelector('cart-ship-date-picker');
    if (!picker || picker.selectedDate) return null;
    return picker;
  }

  function blockCheckout(picker, event) {
    event.preventDefault();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    if (typeof picker.expand === 'function') picker.expand();
    if (typeof picker.openCalendar === 'function') picker.openCalendar();
    picker.showError('Please select a ship date before checking out.');
    if (typeof picker.scrollIntoView === 'function') {
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!form || typeof form.querySelector !== 'function') return;
      if (!form.querySelector('[name="checkout"]')) return;
      const picker = findUnselectedPicker(form);
      if (picker) blockCheckout(picker, event);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      const target = event.target && event.target.closest
        ? event.target.closest('[name="checkout"], a[href="/checkout"], a[href^="/checkout"]')
        : null;
      if (!target) return;
      const picker = findUnselectedPicker(target);
      if (picker) blockCheckout(picker, event);
    },
    true
  );
})();